/**
 * AboutTab Component
 *
 * Form for editing guide's "About" section:
 * - Service Description (detailed description of services)
 * - Specialties (tags/areas of expertise)
 * - Service Area & Radius
 *
 * Epic: MVP 1.21.3 - Guide Dashboard
 * Story: US-1.21.3-12: 5-Tab Profile Editor (8 SP)
 *
 * Props:
 * - profile: object with current profile data
 * - onChange: function(field, value) - Called when field changes
 * - onSave: function() - Called when save button clicked
 * - isSaving: boolean
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Save as _Save, Plus, X, Save } from 'lucide-react';

const SPECIALTY_OPTIONS = [
  'Fly Fishing',
  'Deep Sea Fishing',
  'Bass Fishing',
  'Trophy Hunting',
  'Bow Hunting',
  'Big Game Hunting',
  'Alpine Hiking',
  'Desert Hiking',
  'Backpacking',
  'Rock Climbing',
  'Ice Climbing',
  'Whitewater Kayaking',
  'Sea Kayaking',
  'Wildlife Photography',
  'Bird Watching',
  'Survival Skills',
  'Wilderness First Aid',
  'Backcountry Skiing',
  'Mountaineering',
  'Other',
];

export default function AboutTab({
  profile,
  onChange,
  onSave,
  isSaving = false
}) {
  const [newSpecialty, setNewSpecialty] = useState('');
  const specialties = profile.specialties || [];

  const handleAddSpecialty = () => {
    if (newSpecialty && !specialties.includes(newSpecialty)) {
      onChange('specialties', [...specialties, newSpecialty]);
      setNewSpecialty('');
    }
  };

  const handleRemoveSpecialty = (specialty) => {
    onChange('specialties', specialties.filter(s => s !== specialty));
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900">About Your Services</h2>
          <p className="mt-1 text-sm text-gray-500">
            Describe what you offer and your areas of expertise
          </p>
        </div>

        {/* Service Description */}
        <div>
          <label htmlFor="service_description" className="block text-sm font-medium text-gray-700 mb-1">
            Service Description
          </label>
          <textarea
            id="service_description"
            value={profile.service_description || ''}
            onChange={(e) => onChange('service_description', e.target.value)}
            rows={8}
            placeholder="Describe the experiences and services you offer, what makes them special, typical itineraries, what's included, etc..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            {profile.service_description?.length || 0} characters
          </p>
        </div>

        {/* Specialties */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Specialties & Expertise
          </label>

          {/* Existing Specialties */}
          {specialties.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {specialties.map((specialty) => (
                <span
                  key={specialty}
                  className="inline-flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full"
                >
                  <span>{specialty}</span>
                  <button
                    onClick={() => handleRemoveSpecialty(specialty)}
                    className="hover:text-blue-900 focus:outline-none"
                    aria-label={`Remove ${specialty}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Add Specialty */}
          <div className="flex items-center space-x-2">
            <select
              value={newSpecialty}
              onChange={(e) => setNewSpecialty(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a specialty to add</option>
              {SPECIALTY_OPTIONS.filter(opt => !specialties.includes(opt)).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <button
              onClick={handleAddSpecialty}
              disabled={!newSpecialty}
              className="flex items-center space-x-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              <span>Add</span>
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Select areas where you have expertise or specialized knowledge
          </p>
        </div>

        {/* Service Area */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Area</h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Service Area Radius */}
            <div>
              <label htmlFor="service_radius_miles" className="block text-sm font-medium text-gray-700 mb-1">
                Service Radius (miles)
              </label>
              <input
                type="number"
                id="service_radius_miles"
                value={profile.service_radius_miles || ''}
                onChange={(e) => onChange('service_radius_miles', parseInt(e.target.value) || 0)}
                min="0"
                max="500"
                placeholder="50"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                How far you&apos;re willing to travel
              </p>
            </div>

            {/* Latitude (hidden, managed by map) */}
            <div>
              <label htmlFor="base_location_lat" className="block text-sm font-medium text-gray-700 mb-1">
                Latitude
              </label>
              <input
                type="number"
                id="base_location_lat"
                value={profile.base_location_lat || ''}
                onChange={(e) => onChange('base_location_lat', parseFloat(e.target.value) || 0)}
                step="0.000001"
                placeholder="40.7128"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Longitude (hidden, managed by map) */}
            <div>
              <label htmlFor="base_location_lng" className="block text-sm font-medium text-gray-700 mb-1">
                Longitude
              </label>
              <input
                type="number"
                id="base_location_lng"
                value={profile.base_location_lng || ''}
                onChange={(e) => onChange('base_location_lng', parseFloat(e.target.value) || 0)}
                step="0.000001"
                placeholder="-74.0060"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            💡 Tip: Coordinates will be updated automatically when you select a location on the map (future feature)
          </p>
        </div>

        {/* Save Button */}
        <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onSave}
            disabled={isSaving}
            className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

AboutTab.propTypes = {
  profile: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};
